import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FilterPanel from "./FilterPanel";
import SearchBar from "./SearchBar";
import ActionButtons from "./ActionButtons";
import { CopyButton, PrimaryButton, SoftButton } from "../../UI/DesignStandards";
import { FaPlus, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { getTeamStatus } from "../Hooks/useTournamentFilters";
import {
  STATUS_FILTERS,
  emptyStatusCounts,
} from "./scoringFilterOptions";
import { SCORING_SORT_FIELDS } from "./scoringSort";

const chipClass = (active) =>
  `max-lg:min-h-11 max-lg:inline-flex max-lg:items-center px-3 py-1.5 text-sm rounded-lg font-display font-bold transition-colors ${
    active
      ? "bg-brand-charcoal text-brand-cream"
      : "bg-white text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-charcoal"
  }`;

const sortChipClass = (active) =>
  `inline-flex items-center gap-1 max-lg:min-h-11 px-3 py-1.5 text-sm rounded-lg font-display font-bold transition-colors ${
    active
      ? "bg-brand-charcoal text-brand-cream"
      : "bg-white text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-charcoal"
  }`;

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) {
    return <FaSort className="h-3 w-3 opacity-50" />;
  }
  return sortOrder === "asc" ? (
    <FaSortUp className="h-3 w-3" />
  ) : (
    <FaSortDown className="h-3 w-3" />
  );
};

const ScoringToolbar = ({
  filters = {},
  onFiltersChange = () => {},
  onSearchChange = () => {},
  onClearFilters = () => {},
  onCopyLink = () => {},
  data,
  onCreateTeam,
  onPrintSheets,
  onPrintRecaps,
  onUploadTeams,
  onUploadScores,
  onReviewScores,
  reviewCount = 0,
  onReviewTeams,
  teamReviewCount = 0,
  sortBy = "lane",
  sortOrder = "asc",
  onSortChange = () => {},
  canManage = false,
  canImportSheets = false,
  isPayoutFinalized = false,
  hideTitle = false,
  bracketsHref = "",
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const defaultFilters = {
    search: "",
    squad: "all",
    event: "all",
    status: "all",
    date: "all",
    page: 1,
    ...filters,
  };

  const handleFilterChange = (newFilters) => {
    onFiltersChange({
      ...defaultFilters,
      ...newFilters,
      page: 1,
    });
  };

  const handleStatusChange = (status) => {
    handleFilterChange({ status });
  };

  const statusCounts = useMemo(() => {
    const counts = emptyStatusCounts();
    const teams = data?.teams || [];
    const squadId = defaultFilters.squad;
    const eventId = defaultFilters.event;
    const date = defaultFilters.date;
    const search = String(defaultFilters.search || "").toLowerCase();
    const selectedEvent = (data?.events || []).find(
      (event) => event.id === eventId
    );

    teams.forEach((team) => {
      if (squadId && squadId !== "all" && team.squadID !== squadId) return;
      if (date && date !== "all" && team.squad?.squadDate !== date) return;
      if (eventId && eventId !== "all") {
        const onEvent =
          team.eventsID === eventId ||
          (selectedEvent &&
            (team.bowlers || []).some((bowler) =>
              (bowler.eventEntries || []).some(
                (entry) => entry.name === selectedEvent.eventName
              )
            ));
        if (!onEvent) return;
      }
      if (search) {
        if (search.startsWith("team:")) {
          if (team.id !== search.substring(5)) return;
        } else {
          const nameHit = team.teamName?.toLowerCase().includes(search);
          const bowlerHit = (team.bowlers || []).some((bowler) =>
            bowler.name?.toLowerCase().includes(search)
          );
          if (!nameHit && !bowlerHit) return;
        }
      }
      counts.all += 1;
      const status = getTeamStatus(team);
      if (counts[status] != null) counts[status] += 1;
    });
    return counts;
  }, [data, defaultFilters]);

  const hasActiveFilters =
    defaultFilters.squad !== "all" ||
    defaultFilters.event !== "all" ||
    defaultFilters.status !== "all" ||
    defaultFilters.date !== "all" ||
    Boolean(defaultFilters.search);

  const showReviews =
    canManage &&
    canImportSheets &&
    !isPayoutFinalized &&
    (reviewCount > 0 || teamReviewCount > 0);
  const hasSquads = data?.squads && data.squads.length > 0;
  const hasEvents = data?.events && data.events.length > 0;
  const createDisabled = isPayoutFinalized || !hasSquads || !hasEvents;
  const createTitle = isPayoutFinalized
    ? "Payouts have been locked and cannot be changed"
    : !hasSquads
      ? "Create squads before creating teams"
      : !hasEvents
        ? "Create events before creating teams"
        : "Create a new team";

  return (
    <div className="card-board mb-6">
      <div className="p-4 sm:p-5 space-y-4">
        {hideTitle ? null : (
          <div>
            <h1 className="heading-display text-2xl mb-1">Scoring</h1>
            <p className="text-sm text-brand-muted">
              Enter scores and verify them.
            </p>
          </div>
        )}

        <SearchBar
          value={defaultFilters.search}
          onChange={onSearchChange}
          onClear={onClearFilters}
        />

        <div className="lg:hidden">
          <button
            type="button"
            className="inline-flex items-center min-h-11 px-1 text-sm font-display font-bold text-brand-charcoal"
            aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            {filtersOpen ? "Hide filters" : "Filters and sort"}
          </button>
        </div>

        <div className={filtersOpen ? "space-y-4" : "hidden lg:block space-y-4"}>
        <div className="flex flex-wrap items-center gap-2">
            <FilterPanel
              filters={defaultFilters}
              onFiltersChange={handleFilterChange}
              onClearFilters={onClearFilters}
              squads={data?.squads || []}
              events={data?.events || []}
              compact
              showClear={false}
            />
            <CopyButton
              onCopy={onCopyLink}
              variant="icon"
              title="Copy link with current filters"
              aria-label="Copy link"
            />
            {bracketsHref ? (
              <Link
                to={bracketsHref}
                className="inline-flex items-center max-lg:min-h-11 px-3 py-1.5 text-sm rounded-lg font-display font-bold bg-white text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-charcoal"
              >
                Brackets
              </Link>
            ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => handleStatusChange(filter.id)}
              className={chipClass(defaultFilters.status === filter.id)}
            >
              {filter.label} ({statusCounts[filter.id].toLocaleString("en-US")})
            </button>
          ))}
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="inline-flex items-center max-lg:min-h-11 px-2 py-1.5 text-sm font-display font-bold text-brand-muted hover:text-brand-charcoal"
            >
              Clear
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted">
            Sort
          </span>
          {SCORING_SORT_FIELDS.map((field) => (
            <button
              key={field.id}
              type="button"
              onClick={() => onSortChange(field.id)}
              className={sortChipClass(sortBy === field.id)}
              aria-pressed={sortBy === field.id}
              title={
                sortBy === field.id
                  ? field.id === "score"
                    ? sortOrder === "desc"
                      ? "Score, high to low"
                      : "Score, low to high"
                    : field.id === "lane"
                      ? sortOrder === "asc"
                        ? "Lane, 1 to 99"
                        : "Lane, 99 to 1"
                      : field.id === "added"
                        ? sortOrder === "asc"
                          ? "Added, oldest first"
                          : "Added, newest first"
                        : sortOrder === "asc"
                          ? "Name, A to Z"
                          : "Name, Z to A"
                  : `Sort by ${field.label}`
              }
            >
              {field.label}
              <SortIcon
                field={field.id}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </button>
          ))}
        </div>
        </div>
      </div>

      <div className="px-4 sm:px-5 py-3 border-t border-brand-charcoal/10 space-y-3">
        {canManage && showReviews ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {reviewCount > 0 && onReviewScores ? (
                <SoftButton color="gold" size="sm" onClick={onReviewScores}>
                  Review recaps ({reviewCount.toLocaleString("en-US")})
                </SoftButton>
              ) : null}
              {teamReviewCount > 0 && onReviewTeams ? (
                <SoftButton color="gold" size="sm" onClick={onReviewTeams}>
                  Review signups ({teamReviewCount.toLocaleString("en-US")})
                </SoftButton>
              ) : null}
            </div>
            <PrimaryButton
              onClick={onCreateTeam}
              icon={FaPlus}
              className="flex items-center gap-2"
              disabled={createDisabled}
              title={createTitle}
            >
              Create Team
            </PrimaryButton>
          </div>
        ) : null}

        <ActionButtons
          data={data}
          onCreateTeam={onCreateTeam}
          onPrintSheets={onPrintSheets}
          onPrintRecaps={onPrintRecaps}
          onUploadTeams={onUploadTeams}
          onUploadScores={onUploadScores}
          canManage={canManage}
          canImportSheets={canImportSheets}
          isPayoutFinalized={isPayoutFinalized}
          showCreate={canManage && !showReviews}
        />
      </div>

      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="px-4 sm:px-5 pb-4">
          <p className="text-sm text-brand-muted">
            Page {data.pagination.currentPage} of {data.pagination.totalPages}
            {" · "}
            {data.pagination.totalTeams} teams
          </p>
        </div>
      )}
    </div>
  );
};

export default ScoringToolbar;
